import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CodemaxBrand } from '@/components/Brand'
import { Button, Card, CardHeader, cx, Spinner } from '@/components/ui/primitives'
import { Field, FormGrid, Input } from '@/components/ui/form'
import { ApiError, publicApi, api, requestBlob } from '@/lib/api'
import { BASE } from '@/lib/base'
import { date, money } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import type { CustomerQuotationView } from '@/types/api'

/**
 * Where a customer reads their quotation and signs it.
 *
 * <p>
 * The only screen in the application nobody signs in to reach. It is rendered outside the
 * auth guard entirely - a customer has no ARMS account and never will - and everything it
 * asks the API for goes through `publicApi`, which sends no bearer token and never tries to
 * renew a session that does not exist. See docs/adr/0004-customer-acceptance-link.md.
 * </p>
 */
export function AcceptQuotation() {
  const { token = '' } = useParams()

  const view = useAsync<CustomerQuotationView>(
    (signal) => publicApi.get<CustomerQuotationView>(`/public/acceptance/${token}`, signal),
    [token],
  )

  // A plain address, because this endpoint is anonymous. The browser streams it and
  // handles its own range requests, which is better than holding a whole PDF in memory.
  const document = useDocumentAddress(
    `${BASE}/public/acceptance/${token}/document`,
    view.data?.acceptanceReference ?? null,
  )

  return (
    <AcceptancePage
      view={view.data}
      loading={view.loading}
      error={view.error}
      document={document}
      onAccept={(body) => publicApi.post(`/public/acceptance/${token}/accept`, body)}
      onAccepted={view.reload}
    />
  )
}

/**
 * The same page, for somebody at Codemax.
 *
 * <p>
 * Read-only, and not by hiding the form: `onAccept` is absent, so there is nothing to
 * submit and no endpoint reachable without a token anyway. A rep looking at this is
 * checking what their customer sees, which is worth being able to do without minting a live
 * signing link to find out.
 * </p>
 */
export function AcceptancePreview({ quotationId }: { quotationId: string }) {
  const view = useAsync<CustomerQuotationView>(
    (signal) => api.get<CustomerQuotationView>(`/quotations/${quotationId}/acceptance-preview`, undefined, signal),
    [quotationId],
  )

  // Fetched rather than linked. This endpoint needs the access token and an <object data>
  // carries no Authorization header, so pointing at it directly produced a 401 and the
  // "your browser cannot show the document here" fallback on every staff preview.
  const document = useFetchedDocument(quotationId, view.data?.acceptanceReference ?? null)

  return (
    <AcceptancePage
      view={view.data}
      loading={view.loading}
      error={view.error}
      document={document}
      staffPreview
    />
  )
}

/**
 * Where the document is, and what the download link should point at.
 */
export interface DocumentAddress {
  /** What the viewer loads. Undefined while it is still being fetched. */
  src: string | undefined
  /** Where "Download PDF" goes. Undefined until there is something to download. */
  downloadHref: string | undefined
  /** Set only for a blob, which needs the filename supplied by the anchor. */
  downloadName?: string
  /** Why there is no document, when there is not going to be one. */
  problem?: string
}

/**
 * The anonymous case: the address itself, stamped with the acceptance it carries.
 */
function useDocumentAddress(url: string, reference: string | null): DocumentAddress {
  return useMemo(() => {
    const src = documentSrc(url, reference)

    return { src, downloadHref: `${src}${src.includes('?') ? '&' : '?'}download=true` }
  }, [url, reference])
}

/**
 * The staff case: fetched with the token, held as an object URL.
 *
 * <p>
 * Revoked when the preview goes away or the document changes. An object URL kept for the
 * life of the tab is a whole PDF the browser cannot reclaim, and a rep opens a lot of
 * quotations in a day.
 * </p>
 *
 * Re-fetched when the acceptance reference appears, because signing adds the record of
 * acceptance to the document and the copy already in memory is the one from before it.
 */
function useFetchedDocument(quotationId: string, reference: string | null): DocumentAddress {
  const [src, setSrc] = useState<string | undefined>(undefined)
  const [problem, setProblem] = useState<string | undefined>(undefined)

  useEffect(() => {
    let live = true
    let objectUrl: string | undefined

    setSrc(undefined)
    setProblem(undefined)

    requestBlob(`/quotations/${quotationId}/pdf`, { query: { download: false } })
      .then((blob) => {
        if (!live) return

        objectUrl = URL.createObjectURL(blob)
        setSrc(objectUrl)
      })
      .catch((cause: unknown) => {
        if (!live) return

        // Said, not swallowed. A fetch that fails and leaves the spinner turning is the
        // same dead end as the one this replaced, only quieter.
        setProblem(cause instanceof ApiError ? cause.message : 'Could not build the document.')
      })

    return () => {
      live = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [quotationId, reference])

  return { src, downloadHref: src, downloadName: 'quotation.pdf', problem }
}

interface AcceptSubmission {
  signerName: string
  signerJobTitle: string
  signerEmail: string
  affirmedAuthority: boolean
}

function AcceptancePage({
  view,
  loading,
  error,
  document,
  staffPreview = false,
  onAccept,
  onAccepted,
}: {
  view: CustomerQuotationView | undefined
  loading: boolean
  error: ApiError | undefined
  document: DocumentAddress
  staffPreview?: boolean
  onAccept?: (body: AcceptSubmission) => Promise<unknown>
  onAccepted?: () => void
}) {
  // Nobody signs a document they have not been shown. Held here rather than inside the
  // frame because it is the form that has to wait for it.
  const [documentSettled, setDocumentSettled] = useState(false)

  if (loading && !view) {
    return (
      <Frame>
        <div role="status" className="flex items-center justify-center gap-2.5 py-20 text-muted">
          <Spinner className="size-4" label={null} />
          <span className="text-[13px]">Loading your quotation...</span>
        </div>
      </Frame>
    )
  }

  // A token that opens nothing gets the API's own words, which deliberately say nothing
  // about whether a quotation exists behind it.
  if (error || !view) {
    return (
      <Frame>
        <Card>
          <div className="px-6 py-10 text-center">
            <h1 className="text-[17px] font-semibold">
              {error?.title ?? 'This link is not available'}
            </h1>
            <p className="mx-auto mt-2 max-w-md text-[13px] text-muted">
              {error?.detail ?? 'Check the link in your email, or ask your Codemax contact for a new one.'}
            </p>
          </div>
        </Card>
      </Frame>
    )
  }

  return (
    <Frame>
      {staffPreview && (
        <p className="rounded-lg border border-line-soft bg-wash px-4 py-3 text-[12.5px] text-muted">
          This is what the customer sees. Nothing here can be filled in or signed from inside
          QuoteHero - only the customer's own link can accept a quotation.
        </p>
      )}

      <Header view={view} />
      <DocumentFrame document={document} onSettled={() => setDocumentSettled(true)} />

      {view.state === 'Accepted' ? (
        <AcceptedRecord view={view} />
      ) : view.state === 'Open' ? (
        <AcceptForm
          view={view}
          onAccept={onAccept}
          onAccepted={onAccepted}
          documentSettled={documentSettled}
        />
      ) : (
        <Unavailable state={view.state} />
      )}

      <p className="pb-10 text-center text-[12px] text-muted">
        Prepared by {view.preparedBy} · {view.issuedBy}
      </p>
    </Frame>
  )
}

/**
 * The page's own chrome.
 *
 * The Codemax mark, never the QuoteHero one: this page is part of what a customer receives,
 * and the quotation is issued by Codemax rather than by the tool that produced it.
 */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-full bg-wash">
      {/* Wider than a reading column, because the thing being read is an A4 page. At
          3xl the embedded viewer had about 700px to draw 794px of paper in, so it
          shrank the page to fit and the customer was deciding on something they had to
          zoom into. 5xl gives the document its own width at full size, and the form
          below it is a two-column grid that fills the same width without stretching. */}
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-4 py-8">
        <CodemaxBrand className="h-8 self-start" />
        {children}
      </div>
    </div>
  )
}

function Header({ view }: { view: CustomerQuotationView }) {
  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4 px-6 py-5">
        <div className="min-w-0">
          {view.quotationNo && (
            <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
              {view.quotationNo}
            </p>
          )}
          <h1 className="mt-1 text-[19px] font-semibold">
            {view.title || `Quotation for ${view.customerName}`}
          </h1>
          <p className="mt-1 text-[13px] text-muted">
            For {view.customerName} · issued {date(view.issueDate)}
          </p>
        </div>

        <div className="text-right">
          <p className="text-[19px] font-semibold tabular">
            {money(view.grandTotal, view.currencySymbol, view.currencyDecimals)}
          </p>
          {/* The deadline the document itself prints, said again where the customer is
              deciding whether to act on it today. */}
          {view.validUntil && (
            <p className="mt-1 text-[12.5px] text-muted">
              Valid until {date(view.validUntil)}
              {view.daysRemaining !== null &&
                view.state === 'Open' &&
                ` · ${view.daysRemaining} day${view.daysRemaining === 1 ? '' : 's'} left`}
            </p>
          )}
        </div>
      </div>
    </Card>
  )
}

/**
 * The quotation itself, in the page.
 *
 * <p>
 * An object rather than a fetch into a blob: the browser's own PDF viewer handles paging,
 * zoom and printing, and re-implementing any of that would be worse at all three. The
 * download beneath it is the same bytes with a filename, for the copy that gets filed.
 * </p>
 */
function DocumentFrame({
  document,
  onSettled,
}: {
  document: DocumentAddress
  onSettled?: () => void
}) {
  const { src, downloadHref, downloadName, problem } = document
  const [drawn, setDrawn] = useState(false)

  // A new address is a different document, and it has not been drawn yet. Without this the
  // spinner stays gone across the reload that follows signing, and the customer watches an
  // empty rectangle instead.
  useEffect(() => setDrawn(false), [src])

  const settled = useRef(onSettled)
  settled.current = onSettled

  // Settled means "stopped trying", not "drawn". Three ways to get there, and the last one
  // is why there is a timer at all: some browsers never fire load for an embedded PDF, and
  // one that refuses the embed entirely shows the download fallback instead - in both cases
  // the customer can read the document, and a form that stayed disabled would be the worse
  // failure. The wait is generous because a quotation is megabytes over a phone connection.
  useEffect(() => {
    if (drawn || problem !== undefined) {
      settled.current?.()
      return
    }

    if (src === undefined) return

    const timer = setTimeout(() => settled.current?.(), 15_000)

    return () => clearTimeout(timer)
  }, [drawn, problem, src])

  return (
    <Card>
      <CardHeader
        title="The quotation"
        action={
          <a
            href={downloadHref ?? '#'}
            download={downloadName}
            aria-disabled={downloadHref === undefined}
            className={cx(
              'text-[13px] font-medium text-teal underline-offset-2 hover:underline',
              // Nothing to download until it has arrived. Disabled rather than hidden, so
              // the control does not appear from nowhere once the fetch lands.
              downloadHref === undefined && 'pointer-events-none opacity-40',
            )}
          >
            Download PDF
          </a>
        }
      />

      <div className="px-5 pb-5">
        {/* Tall enough that a full page is on screen at once on a laptop, with a floor so
            a short window does not reduce the viewer to a letterbox. */}
        <div className="relative h-[82vh] min-h-[34rem] w-full overflow-hidden rounded-md border border-line-soft bg-paper">
          {/* Behind the viewer rather than in place of it. A PDF is megabytes over a phone
              connection, and an empty rectangle for those seconds reads as a page that
              failed. Underneath, so that a browser which never fires load - some do not for
              an embedded PDF - still shows the document the moment it paints over this,
              instead of hiding it behind a spinner that would never clear. */}
          {problem !== undefined ? (
            <div className="absolute inset-0 grid place-items-center px-6 text-center">
              <p className="text-[13px] text-muted">{problem}</p>
            </div>
          ) : (
            !drawn && (
              <div
                role="status"
                className="absolute inset-0 grid place-items-center text-muted"
              >
                <div className="flex flex-col items-center gap-2.5">
                  <Spinner className="size-5" label={null} />
                  <span className="text-[13px]">Loading the quotation...</span>
                </div>
              </div>
            )
          )}

          {/* Keyed, not just re-pointed: changing an <object>'s data does not reliably make
              a browser re-fetch an embedded PDF, and remounting it does. Not drawn at all
              until there is an address: an <object> with no data shows its fallback, which
              would tell a customer their browser cannot open a document nobody has fetched
              yet. */}
          {src !== undefined && (
          <object
            key={src}
            data={src}
            type="application/pdf"
            onLoad={() => setDrawn(true)}
            className="relative h-full w-full"
            aria-label="The quotation document"
          >
            {/* Some mobile browsers refuse to embed a PDF at all. They get the link rather
                than an empty grey rectangle. */}
            <div className="grid h-full place-items-center bg-paper px-6 text-center">
              <p className="text-[13px] text-muted">
                Your browser cannot show the document here.{' '}
                <a href={downloadHref ?? '#'} download={downloadName} className="text-teal underline">
                  Download it instead
                </a>
                .
              </p>
            </div>
          </object>
          )}
        </div>
      </div>
    </Card>
  )
}

/**
 * The address of the document to show, stamped with the acceptance it carries.
 *
 * <p>
 * Signing adds the record of acceptance to the document, so the paper on screen after a
 * signature is not the paper that was on screen before it. Reloading the view alone leaves
 * the browser showing the copy it already fetched, and the customer watches their own
 * signature fail to appear on it.
 * </p>
 *
 * The reference goes from absent to present at exactly the moment the document changes,
 * which makes it the honest thing to key on - a timestamp or a counter would change on
 * renders where the document did not. It is not a secret: it is printed on the page the
 * address returns.
 *
 * Exported for the test, because a URL that quietly stops busting the cache is a bug nobody
 * sees until a customer says their signature is missing.
 */
export function documentSrc(url: string, reference: string | null): string {
  if (!reference) return url

  return `${url}${url.includes('?') ? '&' : '?'}accepted=${encodeURIComponent(reference)}`
}

function AcceptForm({
  view,
  onAccept,
  onAccepted,
  documentSettled,
}: {
  view: CustomerQuotationView
  onAccept?: (body: AcceptSubmission) => Promise<unknown>
  onAccepted?: () => void
  /** Whether the document above has finished loading. Signing waits for it. */
  documentSettled: boolean
}) {
  // Prefilled from the contact the quotation names, and editable: the person who signs is
  // very often not the person who was quoted.
  const [name, setName] = useState(view.contactName ?? '')
  const [jobTitle, setJobTitle] = useState(view.contactJobTitle ?? '')
  const [email, setEmail] = useState(view.contactEmail ?? '')
  const [affirmed, setAffirmed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  // Read-only for a member of staff looking at the preview, and for everybody while the
  // document is still arriving: a signature is a statement about a document, and the person
  // giving it should have had the chance to read the one they are signing.
  const readOnly = !onAccept || !documentSettled

  const terms = useMemo(() => {
    if (!view.termsDocumentName) return 'the terms and conditions attached to this quotation'

    return view.termsVersionNo
      ? `the ${view.termsDocumentName} (version ${view.termsVersionNo}) attached to this quotation`
      : `the ${view.termsDocumentName} attached to this quotation`
  }, [view.termsDocumentName, view.termsVersionNo])

  const complete =
    name.trim().length > 0 && jobTitle.trim().length > 0 && email.trim().length > 0 && affirmed

  async function submit() {
    if (!onAccept || !complete) return

    setSaving(true)
    setFailure(null)

    try {
      await onAccept({
        signerName: name.trim(),
        signerJobTitle: jobTitle.trim(),
        signerEmail: email.trim(),
        affirmedAuthority: affirmed,
      })

      onAccepted?.()
    } catch (cause) {
      // Taken back for changes while this page was open. Reloading swaps the whole page for
      // the API's "expired or no longer valid" card, and takes away the document beside a
      // form that can no longer sign it.
      if (cause instanceof ApiError && cause.status === 410) {
        onAccepted?.()
        return
      }

      setFailure(
        cause instanceof ApiError
          ? (cause.detail ?? cause.title)
          : 'Your acceptance could not be recorded. Please try again.',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader
        title="Accept this quotation"
        hint="Your name and the confirmation below together form your signature."
      />

      <div className="flex flex-col gap-4 px-5 py-4">
        <FormGrid>
          <Field label="Full name" required>
            {(id) => (
              <Input
                id={id}
                value={name}
                disabled={readOnly || saving}
                autoComplete="name"
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>

          <Field label="Job title" required>
            {(id) => (
              <Input
                id={id}
                value={jobTitle}
                disabled={readOnly || saving}
                autoComplete="organization-title"
                onChange={(event) => setJobTitle(event.target.value)}
              />
            )}
          </Field>

          <Field label="Email address" required>
            {(id) => (
              <Input
                id={id}
                type="email"
                value={email}
                disabled={readOnly || saving}
                autoComplete="email"
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>
        </FormGrid>

        {/* The tick is the signature, so the sentence it sits against is the one that gets
            recorded and reproduced. It names the company and the exact terms document. */}
        <label className="flex items-start gap-3 rounded-md border border-line-soft px-4 py-3">
          <input
            type="checkbox"
            checked={affirmed}
            disabled={readOnly || saving}
            onChange={(event) => setAffirmed(event.target.checked)}
            className="mt-0.5 size-4 shrink-0"
          />
          <span className="text-[13px]">
            I have authority to accept this quotation on behalf of{' '}
            <strong>{view.customerName}</strong>, and I accept {terms}.
          </span>
        </label>

        {failure && (
          <p className="rounded-md bg-crit-wash px-3 py-2 text-[12.5px] text-crit">{failure}</p>
        )}

        <div className="flex items-center justify-end gap-3">
          {onAccept && !documentSettled && (
            <span role="status" className="mr-auto flex items-center gap-2 text-[12.5px] text-muted">
              <Spinner className="size-3.5" label={null} />
              Waiting for the quotation to load.
            </span>
          )}

          <Button
            variant="primary"
            loading={saving}
            disabled={readOnly || !complete}
            onClick={() => void submit()}
          >
            Accept quotation
          </Button>
        </div>
      </div>
    </Card>
  )
}

/** What a customer sees on returning to a link they already used. */
function AcceptedRecord({ view }: { view: CustomerQuotationView }) {
  return (
    <Card>
      <div className="px-6 py-6">
        <h2 className="text-[15px] font-semibold">This quotation has been accepted</h2>

        <p className="mt-2 text-[13px] text-muted">
          {view.signedByName ? (
            <>
              Accepted by {view.signedByName}
              {view.signedByJobTitle ? `, ${view.signedByJobTitle}` : ''} on{' '}
              {date(view.signedAt)}.
            </>
          ) : (
            <>Accepted on {date(view.signedAt)}.</>
          )}{' '}
          The document above records it. Your Codemax contact has been told.
        </p>

        {view.acceptanceReference && (
          <div className="mt-4 rounded-md border border-line-soft bg-wash px-4 py-3">
            <p className="font-mono text-[13px]">{view.acceptanceReference}</p>
            <p className="mt-1 text-[12px] text-muted">
              Your acceptance reference. It is printed on the document above, and anyone can use
              it to confirm this acceptance
              {view.verificationUrl ? (
                <>
                  {' at '}
                  <a
                    href={`${view.verificationUrl.replace(/\/$/, '')}/verify/${view.acceptanceReference}`}
                    className="text-teal underline-offset-2 hover:underline"
                  >
                    {view.verificationUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>
                </>
              ) : null}
              .
            </p>

            {view.acceptanceSha256 && (
              <p className="mt-2 break-all font-mono text-[11px] text-muted">
                Fingerprint {view.acceptanceSha256}
              </p>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}

function Unavailable({ state }: { state: 'Expired' | 'Withdrawn' }) {
  return (
    <Card>
      <div className="px-6 py-6">
        <h2 className="text-[15px] font-semibold">
          {state === 'Expired' ? 'This quotation has expired' : 'This quotation is no longer offered'}
        </h2>

        <p className="mt-2 text-[13px] text-muted">
          {state === 'Expired'
            ? 'The validity period has passed, so it can no longer be accepted. Ask your Codemax contact for a current quotation - you can still read and download this one above.'
            : 'It has been withdrawn or replaced. Please speak to your Codemax contact - you can still read and download it above.'}
        </p>
      </div>
    </Card>
  )
}
